<template>
  <div class="form-root">
    <header class="form-header">
      <MOPButton
        :label="isEdit ? 'Back to Detail' : 'Back'"
        icon="pi pi-arrow-left"
        class="p-button-text p-button-sm"
        @click="goBack"
      />
      <h1 class="form-title">{{ isEdit ? 'Edit SOP Document' : 'New SOP Document' }}</h1>
    </header>

    <div class="form-card">
      <div class="form-grid">

        <!-- Title -->
        <div class="col-span-2">
          <MOPInputText
            v-model="form.Title"
            label="Title"
            :required="true"
            placeholder="Enter SOP title"
          />
        </div>

        <!-- Category -->
        <MOPDropdown
          v-model="form.Category"
          label="Category"
          :options="categoryOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select category"
          :required="true"
        />

        <!-- Version -->
        <MOPInputText
          v-model="form.Version"
          label="Version"
          placeholder="e.g. v1.0"
        />

        <!-- Description -->
        <div class="col-span-2">
          <label class="upload-label">Description</label>
          <textarea
            v-model="form.Description"
            rows="4"
            class="plain-textarea"
            placeholder="Full description or procedure outline..."
          />
        </div>

        <!-- Document file upload -->
        <div class="col-span-2">
          <label class="upload-label">Document File (PDF, PPTX, DOCX, XLSX)</label>
          <div class="file-upload-zone" @dragover.prevent @drop.prevent="onDrop">
            <input
              v-if="!form.FilePath && !uploading"
              ref="fileInputRef"
              type="file"
              accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx"
              class="native-file-input"
              @change="onFileChange"
            />
            <div v-if="uploading" class="upload-progress">
              <i class="pi pi-spin pi-spinner"></i>
              <span>Uploading…</span>
            </div>
            <div v-else-if="form.FilePath" class="upload-preview">
              <i class="pi pi-check-circle" style="color:#16a34a"></i>
              <span class="upload-filename">{{ uploadedFileName }}</span>
              <button class="clear-upload" @click.stop="form.FilePath = ''; uploadedFileName = ''">
                <i class="pi pi-times"></i>
              </button>
            </div>
            <div v-else class="upload-placeholder">
              <i class="pi pi-upload upload-icon"></i>
              <span class="upload-hint">Click to browse or drag &amp; drop</span>
              <span class="upload-sub">PDF, PPT, PPTX, DOC, DOCX, XLS, XLSX — max 20 MB</span>
            </div>
          </div>
        </div>

        <!-- IsActive -->
        <div class="toggle-field">
          <label class="upload-label">Active</label>
          <input type="checkbox" v-model="form.IsActive" class="toggle-check" />
        </div>

      </div>

      <div class="form-actions">
        <MOPButton label="Cancel" icon="pi pi-times" class="p-button-outlined p-button-secondary" @click="goBack" />
        <MOPButton
          :label="isEdit ? 'Save Changes' : 'Create'"
          icon="pi pi-check"
          :loading="saving"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  getSOPDocumentById,
  createSOPDocument,
  updateSOPDocument,
  getDashboardUserContext
} from '../../services/dashboardService'

const router = useRouter()
const route = useRoute()

const id = Number(route.params.id)
const isEdit = !!id

const ctx = getDashboardUserContext()
if (!ctx.canManageSOP) router.replace('/sop-documents')

const saving = ref(false)
const uploading = ref(false)
const uploadedFileName = ref('')
const fileInputRef = ref<HTMLInputElement | null>(null)

async function uploadFile(file: File) {
  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)
    const token = localStorage.getItem('token') ?? ''
    const rolesData = localStorage.getItem('RolesData')
    const sessionId = rolesData ? (JSON.parse(rolesData).DeptRoleMapId?.toString() ?? '') : ''
    const baseUrl = (import.meta.env.VITE_API_URL as string) || ''
    const res = await fetch(
      `${baseUrl}/PlatformCoreServices/Authorize/AuthorizationFileUpload/UploadFile?FolderName=SOPDocuments`,
      {
        method: 'POST',
        headers: { 'X-Auth-Token': token, 'SessionID': sessionId },
        body: formData
      }
    )
    const data = await res.json()
    form.FilePath = data?.Url ?? data?.url ?? ''
    uploadedFileName.value = file.name
  } catch {
    /* upload failed */
  } finally {
    uploading.value = false
  }
}

async function onFileChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (file) await uploadFile(file)
}

async function onDrop(e: DragEvent) {
  const file = e.dataTransfer?.files?.[0]
  if (file) await uploadFile(file)
}

const form = reactive({
  Id: 0,
  Title: '',
  Description: '',
  Category: 'Clinical',
  Version: '',
  FilePath: '',
  DepartmentId: null as number | null,
  OrganizationId: ctx.orgId,
  BranchId: ctx.branchId,
  IsActive: true,
  CreatedBy: ctx.userId,
  UpdatedBy: ctx.userId
})

const categoryOptions = [
  { label: 'Clinical', value: 'Clinical' },
  { label: 'HR', value: 'HR' },
  { label: 'Pharmacy', value: 'Pharmacy' },
  { label: 'Emergency', value: 'Emergency' },
  { label: 'Admin', value: 'Admin' },
  { label: 'Compliance', value: 'Compliance' }
]


async function handleSubmit() {
  if (!form.Title?.trim() || !form.Category) return
  saving.value = true
  try {
    if (isEdit) {
      await updateSOPDocument({ ...form, UpdatedBy: ctx.userId })
      router.push(`/sop-documents/${id}`)
    } else {
      const newId = await createSOPDocument({ ...form })
      router.push(newId ? `/sop-documents/${newId}` : '/sop-documents')
    }
  } finally {
    saving.value = false
  }
}

function goBack() {
  if (isEdit) router.push(`/sop-documents/${id}`)
  else router.push('/sop-documents')
}

onMounted(async () => {
  if (isEdit) {
    const item = await getSOPDocumentById(id)
    if (item) {
      Object.assign(form, {
        Id: item.Id,
        Title: item.Title ?? '',
        Description: item.Description ?? '',
        Category: item.Category ?? 'Clinical',
        Version: item.Version ?? '',
        FilePath: item.FilePath ?? '',
        DepartmentId: item.DepartmentId ?? null,
        IsActive: item.IsActive ?? true
      })
    }
  }
})
</script>

<style scoped>
.form-root { min-height: 100vh; background: #f1f5f9; padding: 1.5rem; font-family: 'Inter', sans-serif; }
.form-header { margin-bottom: 1rem; }
.form-title { font-size: 1.4rem; font-weight: 700; color: #0f172a; margin: 0.5rem 0 0 0; }
.form-card { max-width: 860px; margin: 0 auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 2rem; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem 1.5rem; margin-bottom: 1.5rem; }
.col-span-2 { grid-column: span 2; }
.upload-label { display: block; font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.5rem; }
.upload-preview { display: flex; align-items: center; gap: 6px; margin-top: 0.5rem; font-size: 0.82rem; color: #475569; }
.clear-upload { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 2px; }
.clear-upload:hover { color: #ef4444; }
.plain-textarea { width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; font-size: 0.875rem; color: #1e293b; background: #f8fafc; outline: none; resize: vertical; transition: border-color 0.15s; font-family: inherit; }
.plain-textarea:focus { border-color: #5eead4; background: #fff; }
.file-upload-zone {
  position: relative;
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  background: #f8fafc;
  min-height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.file-upload-zone:hover { border-color: #5eead4 !important; background: #f0fdfa !important; }
.native-file-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}
.file-upload-zone:hover { border-color: #5eead4; background: #f0fdfa; }
.upload-placeholder { display: flex; flex-direction: column; align-items: center; gap: 4px; pointer-events: none; }
.upload-icon { font-size: 1.5rem; color: #94a3b8; margin-bottom: 4px; }
.upload-hint { font-size: 0.875rem; color: #475569; font-weight: 500; }
.upload-sub { font-size: 0.75rem; color: #94a3b8; }
.upload-progress { display: flex; align-items: center; gap: 8px; color: #0d9488; font-size: 0.875rem; }
.upload-preview { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: #334155; width: 100%; }
.upload-filename { flex: 1; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.clear-upload { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 2px; margin-left: auto; flex-shrink: 0; }
.clear-upload:hover { color: #ef4444; }
.toggle-field { display: flex; flex-direction: column; gap: 4px; }
.toggle-check { width: 18px; height: 18px; cursor: pointer; accent-color: #0d9488; }
.form-actions { display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid #f1f5f9; padding-top: 1.25rem; margin-top: 0.5rem; }
</style>
